import React, { useState } from "react"
import { useSelector } from "react-redux"
import Barchart from "../DailyCalendarFiles/barchart"
import {
	FaCalendarDay,
	FaChevronRight,
	FaUserFriends,
	FaUserNurse,
	FaExclamationTriangle,
	FaDollarSign,
	FaSyringe,
	FaChartBar,
	FaEye
} from "react-icons/fa"
import { getDayOfWeekAndDate, isCenterClosed } from "../../utils/generalUtils"
import StaffingStatusBadge from "./shared/StaffingStatus"

const getStatusColor = issues => {
	if (issues >= 10) return "text-red-500" // Needs Attention
	if (issues >= 5) return "text-yellow-500" // At Risk
	return "text-green-600" // On Target
}

const generateTimeSlots = (
	templateUtilization,
	defaultStart = 8 * 60,
	defaultEnd = 19 * 60,
	step = 60
) => {
	let start = defaultStart
	let end = defaultEnd
	const keys = templateUtilization ? Object.keys(templateUtilization).map(Number) : []
	if (keys.length > 0) {
		start = Math.min(...keys)
		end = Math.max(...keys)
	}
	const slots = []
	for (let t = start; t <= end; t += step) {
		slots.push(t)
	}
	return slots
}

const formatForBarchart = day => {
	const templateUtilization = day.graphData?.templateUtilization || {}
	const timeSlots = generateTimeSlots(templateUtilization)
	const scheduleData = day.graphData?.utilizationCurves?.scheduleData || []

	// Create a map of time slots to their data
	const scheduleMap = {}
	scheduleData.forEach(slot => {
		const t = typeof slot.time === "string" ? parseInt(slot.time, 10) : slot.time
		scheduleMap[t] = slot
	})

	const utilization = timeSlots.map(time => {
		const slot = scheduleMap[time] || {}
		const scheduled = templateUtilization[time] || 0
		return {
			time,
			expectedPatients: scheduled,
			scheduled: slot.ongoing ?? 0,
			starts: slot.starts ?? 0,
			acuity: slot.acuity ?? 0
		}
	})

	return {
		date: day.date,
		utilization,
		template: templateUtilization,
		numberOfChairs: 45
	}
}

const FutureHuddleDayOverview = ({
	futureHuddleData,
	settings,
	setSelectedDay,
	schedulesLoading = false
}) => {
	const centerOpen = settings.centerOpen
	const futureSchedulesData = useSelector(state => state.futureSchedules.data)
	const [showDetailedView, setShowDetailedView] = useState(true)

	return (
		<div className="mt-8">
			<div className="flex items-center justify-between mb-6">
				<div className="flex items-center">
					<FaCalendarDay style={{ color: "#352D66" }} className="mr-2 text-xl" />
					<h2 className="text-2xl font-semibold" style={{ color: "#352D66" }}>
						Daily Overview
					</h2>
				</div>

				{/* View Toggle */}
				<div className="flex items-center space-x-2">
					<div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
						<button
							onClick={() => setShowDetailedView(false)}
							className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
								!showDetailedView
									? "bg-white text-gray-900 shadow-sm"
									: "text-gray-600 hover:text-gray-900"
							}`}
						>
							<FaEye className="mr-1.5 text-xs" />
							Simplified
						</button>
						<button
							onClick={() => setShowDetailedView(true)}
							className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
								showDetailedView
									? "bg-white text-gray-900 shadow-sm"
									: "text-gray-600 hover:text-gray-900"
							}`}
						>
							<FaChartBar className="mr-1.5 text-xs" />
							Detailed
						</button>
					</div>
				</div>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
				{futureHuddleData.map((day, idx) => {
					const { day: dayOfWeek, date } = getDayOfWeekAndDate(day.date)
					const patients = day.metrics?.current_scheduled_patients || 0
					const scheduledAM = day.metrics?.scheduled_patients_am || 0
					const scheduledPM = day.metrics?.scheduled_patients_pm || 0
						const goal = day.metrics?.target_patients || 0
						const underGoal = goal > 0 ? Math.max(goal - patients, 0) : 0

					// Extract nurse recommendations data like in NurseSummary.jsx
					const nurseRecommendationsData = day.metrics?.nurse_recommendations || {
						recommendedRNs: 0,
						recommendedLVNs: 0
					}
					const recommendedRNs = nurseRecommendationsData.recommendedRNs || 0
					const recommendedLVNs = nurseRecommendationsData.recommendedLVNs || 0
					const totalNurses = recommendedRNs + recommendedLVNs

					// Get current nurse schedule for this day from futureSchedules Redux state
					const daySchedule = futureSchedulesData?.[day.date]
					const currentNurseSchedule = daySchedule?.nurses || []
					const currentStaffingCount = currentNurseSchedule.length

					// Check if schedule data has been loaded for this date
					const scheduleDataLoaded = !!daySchedule

					const issues = Array.isArray(day.schedulingIssues)
						? day.schedulingIssues.length
						: 0
					const statusColor = getStatusColor(issues)

					// Extract high cost drugs and high acuity infusions data
					const highCostDrugs = day.metrics?.high_cost_drugs || 0
					const highAcuityInfusions = day.metrics?.high_acuity_infusions || {}
					const dateObj = new Date(day.date + "T12:00:00-08:00")
					const centerIsClosed = isCenterClosed(
						dateObj,
						centerOpen,
						settings.center_closed_dates || []
					)

					// Don't render cards for closed centers
					if (centerIsClosed) {
						return null
					}

					return (
						<div
							key={day.date}
							className={`bg-white rounded-lg shadow hover:shadow-xl transition-shadow duration-200 overflow-hidden flex flex-col ${
								showDetailedView ? "min-h-96" : "min-h-80"
							}`}
						>
							<div
								className={`${showDetailedView ? "p-4" : "p-3"} border-b border-gray-100`}
							>
								<div className="flex items-center justify-between">
									<h3 className="text-lg font-semibold text-gray-900">
										{dayOfWeek},{" "}
										<span style={{ color: "#352D66" }}>{date}</span>
									</h3>

									<button
										onClick={() => setSelectedDay(day)}
										className="flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors duration-150 group"
										style={{
											background:
												"linear-gradient(90deg, rgba(99,102,241,0.1) 0%, rgba(99,102,241,0.05) 100%)",
											borderColor: "rgba(99,102,241,0.2)",
											color: "rgb(99,102,241)",
											border: "1px solid rgba(99,102,241,0.2)"
										}}
									>
										View Details
										<FaChevronRight className="ml-1.5 text-xs group-hover:translate-x-0.5 transition-transform" />
									</button>
								</div>

								<div
									className={`${showDetailedView ? "mt-4" : "mt-3"} grid grid-cols-2 gap-3`}
								>
									<div className="flex items-center">
										<div className="p-1.5 bg-indigo-100 rounded-full mr-2">
											<FaUserFriends
												style={{ color: "#352D66" }}
												className="text-xs"
											/>
										</div>
										<div className="flex-1">
											<div className="text-xs text-gray-500">Scheduled</div>
											<div className="font-semibold">
												{patients} patients
												{
													<span className="text-sm font-normal text-gray-600 ml-2">
														• {scheduledAM} AM • {scheduledPM} PM
													</span>
												}
											</div>
											<div className="text-xs font-normal mt-0.5">
												{goal > 0 ? (
													underGoal > 0 ? (
														<span className="text-amber-600 font-medium">
															Goal {goal} · {underGoal} under
														</span>
													) : (
														<span className="text-green-600 font-medium">
															Goal {goal} · on target
														</span>
													)
												) : (
													<span className="text-gray-400">No goal set</span>
												)}
											</div>
										</div>
									</div>

									<div className="flex items-center">
										<div className="p-1.5 bg-indigo-100 rounded-full mr-2">
											<FaUserNurse
												style={{ color: "#352D66" }}
												className="text-xs"
											/>
										</div>
										<div className="flex-1">
											<div className="text-xs text-gray-500">Scheduled</div>
											<div className="font-semibold">
												{scheduleDataLoaded ? (
													<>
														{currentStaffingCount} nurses
														{currentNurseSchedule.length > 0 && (
															<span className="text-sm font-normal text-gray-600 ml-2">
																•{" "}
																{
																	currentNurseSchedule.filter(
																		n => n.nurseRole === "RN"
																	).length
																}{" "}
																RN •{" "}
																{
																	currentNurseSchedule.filter(
																		n => n.nurseRole === "LVN"
																	).length
																}{" "}
																LVN
															</span>
														)}
													</>
												) : (
													<span className="text-gray-400">
														Loading...
													</span>
												)}
											</div>
											<div className="mt-1">
												{scheduleDataLoaded ? (
													<StaffingStatusBadge
														currentStaffingCount={currentStaffingCount}
														recommendedNursesCount={totalNurses}
														currentNurseSchedule={currentNurseSchedule}
														size="sm"
														className="inline-flex"
													/>
												) : (
													<div className="px-2 py-0.5 text-xs rounded-full font-medium flex items-center gap-2 bg-gray-100 text-gray-500">
														<div className="animate-spin h-3 w-3 border-2 border-gray-400 border-t-transparent rounded-full"></div>
														<span>Loading...</span>
													</div>
												)}
											</div>
										</div>
									</div>

									<div className="flex items-center">
										<div className="p-1.5 bg-orange-100 rounded-full mr-2">
											<FaExclamationTriangle className="text-orange-600 text-xs" />
										</div>
										<div>
											<div className="text-xs text-gray-500">Issues</div>
											<div className={`font-semibold ${statusColor}`}>
												{issues} issues
											</div>
										</div>
									</div>

									<div className="flex items-center">
										<div className="p-1.5 bg-indigo-100 rounded-full mr-2">
											<FaDollarSign
												style={{ color: "#352D66" }}
												className="text-xs"
											/>
										</div>
										<div>
											<div className="text-xs text-gray-500">
												High Cost Drugs
											</div>
											<div className="font-semibold">
												{highCostDrugs} patients
											</div>
										</div>
									</div>

									<div className="flex items-start">
										<div className="p-1.5 bg-indigo-100 rounded-full mr-2 mt-0.5">
											<FaSyringe
												style={{ color: "#352D66" }}
												className="text-xs"
											/>
										</div>
										<div className="flex-1 min-w-0">
											<div className="text-xs text-gray-500">
												High Task Infusions
											</div>
											<div className="font-semibold text-sm max-h-16 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 border border-gray-200 rounded p-1 bg-gray-50">
												{Object.keys(highAcuityInfusions).length > 0 ? (
													<div className="space-y-0.5">
														{Object.entries(highAcuityInfusions).map(
															([type, count]) => (
																<div key={type} className="text-xs">
																	{type}: {count}
																</div>
															)
														)}
													</div>
												) : (
													<span className="text-gray-400 text-xs">
														None
													</span>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>

							{/* Conditionally render barchart based on view toggle */}
							{showDetailedView && (
								<div className="p-2">
									<Barchart
										analyze={formatForBarchart(day)}
										compact={true}
										viewDetailsAction={() => setSelectedDay(day)}
										showCancellations={false}
										showAddOns={false}
									/>
								</div>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}

// Memoized so an unrelated parent re-render (e.g. a sidebar toggle) doesn't
// re-render the day cards/charts — its props are unchanged in that case.
export default React.memo(FutureHuddleDayOverview)
